import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import { SemanticICONS } from 'semantic-ui-react'

import useHideOnScroll from 'hooks/useHideOnScroll'

import Icon from 'components/elements/Icon'
import FadeInOut from 'components/transitions/FadeInOut'

import styles from './floatingToolbar.module.scss'

const MIN_HEIGHT = 70
const TOTAL_MAX_HEIGHT_PERCENT = 0.75

export type Option = {
  label: ReactNode
  icon: SemanticICONS
  onClick?: () => void
  keybind?: string
  disabled?: boolean
  hidden?: boolean
  style?: CSSProperties
  subOptions?: Option[]
}

type Props = {
  primaryOptions: Option[]
  secondaryOptions?: Option[]

  includeShrinkOption?: boolean
  includeMoreOption?: boolean

  className?: string
  hideOnScrollRef?: HTMLDivElement
}

const FloatingToolbar = ({
  primaryOptions,
  secondaryOptions,
  includeMoreOption,
  includeShrinkOption,
  className = '',
  hideOnScrollRef,
}: Props) => {
  const scrollingDown = useHideOnScroll(3, hideOnScrollRef)

  const dragHandle = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const isDragging = useRef(false)
  const startingTouchPosition = useRef<number | null>(null)
  const dragOffset = useRef<number | null>(null)
  const startingHeight = useRef<number | null>(null)

  const [open, setOpen] = useState(false)
  const [height, setHeight] = useState(MIN_HEIGHT)
  const [shrunk, setShrunk] = useState(false)

  const [flingMenuOptions, setFlingMenuOptions] = useState<Option[] | null>(secondaryOptions || null)

  const MAX_HEIGHT = getMaxHeight()

  const handleSetHeight = (desiredHeight: number) => {
    if (desiredHeight > MAX_HEIGHT) return setHeight(MAX_HEIGHT)
    if (desiredHeight < MIN_HEIGHT) return setHeight(MIN_HEIGHT)

    setHeight(desiredHeight)
  }

  const handleTouchStart = (touchEvent: TouchEvent) => {
    if (listRef.current?.scrollTop !== 0) return

    startingTouchPosition.current = touchEvent.touches[0]?.clientY
    startingHeight.current = height
  }

  const handleTouch = (touchEvent: TouchEvent) => {
    if (startingTouchPosition.current === null) return
    if (startingHeight.current === null) return
    if (listRef.current?.scrollTop !== 0) return

    const current = touchEvent.touches[0]?.clientY
    dragOffset.current = startingTouchPosition.current - current
    const jitter = Math.abs(dragOffset.current)

    if (jitter < 50) return // Prevents misfires - don't start listening to drags until we're past our jitter boundry

    isDragging.current = true
    dragHandle.current?.classList.add(styles.activelyDragging)

    handleSetHeight(startingHeight.current + dragOffset.current)
  }

  const handleTouchEnd = () => {
    if (!isDragging.current || !startingHeight.current) {
      // Resetting anything set by handleTouch
      startingHeight.current = null
      startingTouchPosition.current = null

      return
    }

    if (dragOffset.current !== null) {
      const dragThreshold = 2 * MIN_HEIGHT
      const swipeUp = dragOffset.current > 0
      const swipeThresholdMet = Math.abs(dragOffset.current) > dragThreshold

      if (swipeThresholdMet) handleSetOpen(swipeUp)
      else handleSetOpen(!swipeUp)
    }

    isDragging.current = false
    dragOffset.current = null
    startingHeight.current = null
    startingTouchPosition.current = null
    dragHandle.current?.classList.remove(styles.activelyDragging)
  }

  const handleSetOpen = (drawerIsOpen: boolean) => {
    if (drawerIsOpen) {
      setHeight(MAX_HEIGHT)
      setOpen(true)

      document.body.classList.add('no-scrolling')

      return
    }

    setOpen(false)
    setHeight(MIN_HEIGHT)
    setFlingMenuOptions(secondaryOptions || null)

    setTimeout(() => document.body.classList.remove('no-scrolling'), 25) // Dont to prevent overscrolling when swipping "away" the drawer

    listRef.current?.scrollTo({ top: 0 })
  }

  const handleSetHidden = (hidden: boolean) => {
    setShrunk(hidden)

    if (hidden) setOpen(false)
  }

  const handleManualOpenFlingMenu = (options?: Option[] | null) => {
    if (!options) return

    setFlingMenuOptions(options)
    handleSetOpen(true)
  }

  useEffect(() => {
    if (shrunk) return
    if (!flingMenuOptions) return

    dragHandle.current?.addEventListener('touchstart', handleTouchStart, { passive: false })
    dragHandle.current?.addEventListener('touchmove', handleTouch, { passive: false })
    dragHandle.current?.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      dragHandle.current?.removeEventListener('touchstart', handleTouchStart)
      dragHandle.current?.removeEventListener('touchmove', handleTouch)
      dragHandle.current?.removeEventListener('touchend', handleTouchEnd)
    }
  }, [height, shrunk, flingMenuOptions])

  const openPercent = height / MAX_HEIGHT

  return (
    <div
      className={`
        ${styles.container}
        ${shrunk ? styles.shrunk : ''} 
        ${scrollingDown ? styles.hidden : ''}
        ${className}
      `}
      style={{ maxHeight: height }}
      ref={dragHandle}>
      <div className={styles.toolbar}>
        <button className={styles.dragHandle} onClick={() => handleSetOpen(!open)}>
          {!shrunk && !!flingMenuOptions && <span />}
        </button>

        <FadeInOut cssRemovedFromDOMOnClosed show={!open} className={styles.options}>
          {!shrunk &&
            primaryOptions.map((option, index) => (
              <Option
                key={index}
                icon={option.icon}
                label={option.label}
                onClick={option.subOptions ? () => handleManualOpenFlingMenu(option.subOptions) : option.onClick}
                disabled={option.disabled}
              />
            ))}

          {includeMoreOption && secondaryOptions && (
            <Option icon="ellipsis horizontal" label="More" onClick={() => handleSetOpen(true)} />
          )}

          {includeShrinkOption && (
            <Option
              icon={shrunk ? 'arrow circle left' : 'arrow circle right'}
              label={shrunk ? 'Toolbar' : 'Hide'}
              onClick={() => handleSetHidden(!shrunk)}
            />
          )}
        </FadeInOut>

        {!!flingMenuOptions && (
          <FadeInOut ref={listRef} cssRemovedFromDOMOnClosed show={open} className={styles.list} outDuration={100}>
            {flingMenuOptions.map((option, index) => (
              <ListOption
                key={index}
                icon={option.icon}
                label={option.label}
                onClick={() => {
                  if (option.onClick) option.onClick()

                  handleSetOpen(false)
                }}
                disabled={option.disabled}
                keybind={option.keybind}
                style={{ opacity: !open || openPercent === 1 ? 1 : openPercent - 0.2 }}
              />
            ))}
          </FadeInOut>
        )}
      </div>
    </div>
  )
}

export default FloatingToolbar

const Option = ({ icon, label, disabled, onClick }: Option) => (
  <button className={styles.option} onClick={onClick} disabled={disabled}>
    <Icon name={icon} />
    <span>{label}</span>
  </button>
)

const ListOption = ({ icon, label, disabled, onClick, keybind, style }: Option) => (
  <button className={styles.item} onClick={onClick} disabled={disabled} style={style}>
    <span className={styles.primary}>
      <Icon name={icon} />
      <span>{label}</span>
    </span>

    {keybind && <span className={styles.keybind}>{keybind}</span>}
  </button>
)

const getMaxHeight = () => {
  if (typeof window === 'undefined') return MIN_HEIGHT

  const absoluteMax = window?.innerHeight * TOTAL_MAX_HEIGHT_PERCENT // 80% of the height of the page

  return absoluteMax
}
